import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { api, type PalletEtiqueta } from '../api';
import { mensajeDeError, useApi, VIVO } from '../lib/useApi';
import { Cargando, Encabezado, Icono, Numero, PanelError, useConfirmar } from '../components/ui';
import { codigoCabe, enPares, svgEtiquetaPallets } from '../lib/etiquetasPallet';
import { imprimirTirada } from '../lib/imprimir';
import { abrir, aparecer, desplegar, fila, resorte } from '../components/movimiento';
import { altoMinimo, aviso, botonPrimario, botonSecundario, c, campo, enlace, mono, tarjeta } from '../theme';

/**
 * Etiquetas de pallet: la que se pega al bajar un pallet del contenedor.
 *
 * El pallet llega de China con su propia etiqueta —material, cantidad, PO—
 * pero sin ningún código que nuestro escáner pueda leer. Esta pantalla imprime
 * el que hace falta: se elige el contenedor (trae todos los suyos, estén ya
 * confirmados o todavía en el borrador del cliente) o se escribe/escanea un
 * pallet suelto, por ejemplo para reponer una etiqueta rota.
 *
 * El rollo es fijo —100×150 mm, dos pallets por hoja— así que a diferencia de
 * `EtiquetasUbicacion.tsx` acá no hay tamaño ni orientación para elegir.
 */

/** Un pallet sin nada arriba: se le entregó todo, o nunca tuvo una línea cargada. */
const esVacio = (p: PalletEtiqueta) => !p.articulo || p.piezas <= 0;

export default function EtiquetasPallet({
  contenedor,
  onVerContenedor,
}: {
  /** El que viene ya tildado: desde la ficha del contenedor (`#/etiquetasPallet/<id>`). */
  contenedor?: string | null;
  onVerContenedor: (id: string) => void;
}) {
  const { datos: contenedores, cargando, error, recargar } = useApi(() => api.contenedores(), [], VIVO.normal);

  const [elegidos, setElegidos] = useState<string[]>([]);
  // 'cargando' mientras se pide, un string si falló, o la lista si ya llegó.
  const [porContenedor, setPorContenedor] = useState<Record<string, PalletEtiqueta[] | 'cargando' | string>>({});

  const [sueltos, setSueltos] = useState<PalletEtiqueta[]>([]);
  const [textoSuelto, setTextoSuelto] = useState('');
  const [errorSuelto, setErrorSuelto] = useState('');
  const [agregando, setAgregando] = useState(false);

  // Sacar un pallet puntual de la lista final, venga de un contenedor o de los
  // sueltos. No se limpia si se destilda y se vuelve a tildar el contenedor:
  // es el caso raro, y complicar el estado para ese caso no vale la pena.
  const [excluidos, setExcluidos] = useState<Set<string>>(new Set());

  // Los vacíos (sin piezas arriba o sin artículo) no necesitan etiqueta: se
  // dejan afuera salvo que se pidan. Los sueltos se imprimen siempre, porque
  // alguien los tipeó a propósito.
  const [incluirVacios, setIncluirVacios] = useState(false);

  // Los completos salen enteros y no se etiquetan con la descarga: se suman
  // todos de una vez, y el que sobre se saca con la cruz de la lista.
  const [conCompletos, setConCompletos] = useState(false);
  const [completos, setCompletos] = useState<PalletEtiqueta[] | 'cargando' | string | null>(null);

  async function alternarCompletos() {
    const activar = !conCompletos;
    setConCompletos(activar);
    if (!activar || Array.isArray(completos) || completos === 'cargando') return;
    setCompletos('cargando');
    try {
      setCompletos(await api.palletsCompletos());
    } catch (err) {
      setCompletos(mensajeDeError(err));
    }
  }

  const [problema, setProblema] = useState('');
  const [confirmar, dialogo] = useConfirmar();

  // Al abrir con uno elegido: queda tildado y con sus pallets pedidos.
  useEffect(() => {
    if (!contenedor) return;
    setElegidos([contenedor]);
    setPorContenedor({ [contenedor]: 'cargando' });
    api.palletsDeContenedor(contenedor).then(
      (pallets) => setPorContenedor((prev) => ({ ...prev, [contenedor]: pallets })),
      (err) => setPorContenedor((prev) => ({ ...prev, [contenedor]: mensajeDeError(err) })),
    );
  }, [contenedor]);

  if (error) return <PanelError mensaje={error} onReintentar={recargar} />;
  if (cargando && !contenedores) return <Cargando texto="Leyendo los contenedores…" />;
  if (!contenedores) return null;

  // Los que no declararon ningún pallet no tienen nada para imprimir acá.
  // Más nuevo primero: es el que se está descargando ahora.
  const lista = contenedores
    .filter((cnt) => cnt.pallets > 0)
    .sort((a, b) => b.fechaIso.localeCompare(a.fechaIso) || b.id.localeCompare(a.id));

  async function alternarContenedor(id: string) {
    const yaElegido = elegidos.includes(id);
    setElegidos((prev) => (yaElegido ? prev.filter((x) => x !== id) : [...prev, id]));
    if (yaElegido || id in porContenedor) return;

    setPorContenedor((prev) => ({ ...prev, [id]: 'cargando' }));
    try {
      const pallets = await api.palletsDeContenedor(id);
      setPorContenedor((prev) => ({ ...prev, [id]: pallets }));
    } catch (err) {
      setPorContenedor((prev) => ({ ...prev, [id]: mensajeDeError(err) }));
    }
  }

  async function agregarSuelto() {
    const codigo = textoSuelto.trim().toUpperCase();
    if (!codigo) return;
    setAgregando(true);
    setErrorSuelto('');
    try {
      const { pallets: encontrados, desconocidos } = await api.palletsPorCodigo([codigo]);
      if (desconocidos.length > 0) {
        setErrorSuelto(`No encontramos ningún pallet ${desconocidos[0]}.`);
      } else {
        setSueltos((prev) => [...prev.filter((p) => p.id.toUpperCase() !== codigo), ...encontrados]);
        setExcluidos((prev) => {
          if (!prev.has(codigo)) return prev;
          const copia = new Set(prev);
          copia.delete(codigo);
          return copia;
        });
        setTextoSuelto('');
      }
    } catch (err) {
      setErrorSuelto(mensajeDeError(err));
    } finally {
      setAgregando(false);
    }
  }

  function quitar(id: string) {
    const u = id.toUpperCase();
    setSueltos((prev) => prev.filter((p) => p.id.toUpperCase() !== u));
    setExcluidos((prev) => new Set(prev).add(u));
  }

  // Los pallets de los contenedores tildados más los sueltos, sin repetir
  // (por id, igual que el resto del escaneo) y sin lo que se sacó a mano.
  const combinados: PalletEtiqueta[] = [];
  let vaciosOcultos = 0;
  for (const id of elegidos) {
    const r = porContenedor[id];
    if (!Array.isArray(r)) continue;
    for (const p of r) {
      if (!incluirVacios && esVacio(p)) vaciosOcultos++;
      else combinados.push(p);
    }
  }
  if (conCompletos && Array.isArray(completos)) {
    for (const p of completos) {
      if (!incluirVacios && esVacio(p)) vaciosOcultos++;
      else combinados.push(p);
    }
  }
  combinados.push(...sueltos);

  const vistos = new Set<string>();
  const pallets: PalletEtiqueta[] = [];
  for (const p of combinados) {
    const u = p.id.toUpperCase();
    if (vistos.has(u) || excluidos.has(u)) continue;
    vistos.add(u);
    pallets.push(p);
  }

  const total = pallets.length;
  const pares = enPares(pallets);
  const cantEtiquetas = pares.length;
  const noEntran = pallets.filter((p) => !codigoCabe(p.id)).length;
  const preview = pares.slice(0, 8);

  async function imprimir() {
    if (cantEtiquetas === 0) return;
    setProblema('');
    const fallo = await imprimirTirada(
      confirmar,
      { nombre: `Etiquetas de pallet (${cantEtiquetas})`, ancho: 100, alto: 150 },
      pares.map(svgEtiquetaPallets),
      100,
    );
    if (fallo !== null) setProblema(fallo);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Encabezado
        bajada="Elegí el contenedor del que bajaron, o cargá un pallet suelto por su código para reimprimir uno con la etiqueta rota."
      />

      <AnimatePresence>
        {problema && (
          <motion.div key={problema} role="alert" {...abrir(8)} style={aviso('err')}>
            {problema}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Selección arriba y vista previa abajo hasta que entran lado a lado. */}
      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,360px)]">
        {/* ------------------------------------------------------ selección */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <div style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>Contenedores</span>
            {lista.length === 0 ? (
              <span style={{ fontSize: 12.5, color: c.texto3 }}>No hay ningún contenedor con pallets declarados.</span>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(230px,1fr))', gap: 10 }}>
                {lista.map((cnt, i) => {
                  const elegido = elegidos.includes(cnt.id);
                  const estado = porContenedor[cnt.id];
                  return (
                    <motion.label
                      key={cnt.id}
                      {...fila(i)}
                      // La selección cambia de color con transición, no de golpe.
                      animate={{ opacity: 1, y: 0, borderColor: elegido ? c.verde : c.borde, backgroundColor: elegido ? c.verdeTinte : c.panel }}
                      style={{
                        ...tarjeta,
                        padding: 14,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={elegido}
                          onChange={() => void alternarContenedor(cnt.id)}
                          style={{ marginTop: 3, width: 18, height: 18, flex: '0 0 18px' }}
                        />
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: c.texto, fontFamily: mono }}>{cnt.id}</span>
                          <button
                            type="button"
                            onClick={() => onVerContenedor(cnt.id)}
                            style={{ ...enlace, fontFamily: undefined, fontSize: 12.5, alignSelf: 'flex-start', minHeight: 30 }}
                          >
                            Ver ficha
                          </button>
                          <span style={{ fontSize: 12.5, color: c.texto2 }}>
                            {cnt.numero != null ? `N° ${cnt.numero} · ` : ''}
                            {cnt.fecha} · {cnt.pallets} {cnt.pallets === 1 ? 'pallet' : 'pallets'} · {cnt.estado}
                          </span>
                        </span>
                      </span>
                      <AnimatePresence initial={false} mode="wait">
                        {estado === 'cargando' && (
                          <motion.span key="c" {...aparecer} style={{ fontSize: 12, color: c.texto3 }}>
                            Buscando sus pallets…
                          </motion.span>
                        )}
                        {typeof estado === 'string' && estado !== 'cargando' && (
                          <motion.span key="e" {...aparecer} style={{ fontSize: 12, color: c.rojo }}>
                            {estado}
                          </motion.span>
                        )}
                        {Array.isArray(estado) && (
                          <motion.span key="ok" {...aparecer} style={{ fontSize: 12, color: c.texto3 }}>
                            {estado.length} {estado.length === 1 ? 'pallet encontrado' : 'pallets encontrados'}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </motion.label>
                  );
                })}
              </div>
            )}
          </div>

          {/* ------------------------------------------------- pallets completos */}
          <div style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={conCompletos}
                onChange={() => void alternarCompletos()}
                style={{ marginTop: 2, width: 18, height: 18, flex: '0 0 18px' }}
              />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>
                  Pallets completos
                  {Array.isArray(completos) ? ` (${completos.length})` : ''}
                </span>
                <span style={{ fontSize: 12.5, color: c.texto2 }}>
                  Todos los completos que siguen en el depósito, de cualquier contenedor. Los que no quieras, sacalos de la lista.
                </span>
              </span>
            </label>
            <AnimatePresence initial={false} mode="wait">
              {conCompletos && completos === 'cargando' && (
                <motion.span key="c" {...desplegar} style={{ ...desplegar.style, fontSize: 12.5, color: c.texto3 }}>Buscando…</motion.span>
              )}
              {conCompletos && typeof completos === 'string' && completos !== 'cargando' && (
                <motion.span key="e" {...desplegar} style={{ ...desplegar.style, fontSize: 12.5, color: c.rojo }}>{completos}</motion.span>
              )}
              {conCompletos && Array.isArray(completos) && completos.length === 0 && (
                <motion.span key="v" {...desplegar} style={{ ...desplegar.style, fontSize: 12.5, color: c.texto3 }}>No hay pallets completos en el depósito.</motion.span>
              )}
            </AnimatePresence>
          </div>

          {/* --------------------------------------------------- pallet suelto */}
          <div style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <span style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>Pallet suelto</span>
              <div style={{ marginTop: 2, fontSize: 12.5, color: c.texto3 }}>
                Para reimprimir uno solo, p. ej. porque se rompió la etiqueta. Escribí o escaneá su código.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                value={textoSuelto}
                onChange={(e) => {
                  setTextoSuelto(e.target.value);
                  setErrorSuelto('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && void agregarSuelto()}
                placeholder="PLT-0000331"
                style={{ ...campo, flex: '1 1 150px', minWidth: 0, fontFamily: mono }}
              />
              <button
                type="button"
                onClick={() => void agregarSuelto()}
                disabled={agregando}
                style={{ ...botonSecundario, minHeight: altoMinimo(42), padding: '0 16px', opacity: agregando ? 0.6 : 1 }}
              >
                <Icono nombre="add" size={18} />
                Agregar
              </button>
            </div>
            <AnimatePresence>
              {errorSuelto && (
                <motion.span key={errorSuelto} {...aparecer} style={{ fontSize: 12.5, color: c.rojo }}>
                  {errorSuelto}
                </motion.span>
              )}
            </AnimatePresence>
          </div>

          {/* ----------------------------------------------------- lista final */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: c.texto, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={incluirVacios}
              onChange={(e) => setIncluirVacios(e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            <span>
              Incluir pallets vacíos (sin productos)
              {!incluirVacios && vaciosOcultos > 0 && (
                <span style={{ color: c.texto3, fontSize: 12.5 }}>
                  {' '}
                  · {vaciosOcultos} {vaciosOcultos === 1 ? 'vacío queda afuera' : 'vacíos quedan afuera'}
                </span>
              )}
            </span>
          </label>

          <AnimatePresence initial={false}>
          {total > 0 && (
            <motion.div {...abrir(14)} style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>
                Pallets a imprimir (<Numero valor={total} />)
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto', position: 'relative' }}>
                {/* Con cientos de completos no se anima fila por fila ni se reacomoda. */}
                <AnimatePresence initial={false} mode={total <= 40 ? 'popLayout' : 'sync'}>
                {pallets.map((p, i) => (
                  <motion.div
                    key={p.id}
                    layout={total <= 40}
                    {...(total <= 40 ? { ...fila(i), transition: { ...fila(i).transition, layout: resorte } } : {})}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
                  >
                    <span style={{ fontFamily: mono, color: c.texto, flex: '0 0 auto' }}>{p.id}</span>
                    <span style={{ color: c.texto3, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.articulo ?? 'Sin artículo'}
                      {p.articulos > 1 ? ` +${p.articulos - 1}` : ''}
                      {p.contenedorNumero != null ? ` · Cont. N° ${p.contenedorNumero}` : p.contenedor ? ` · ${p.contenedor}` : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => quitar(p.id)}
                      title="Quitar"
                      aria-label={`Quitar ${p.id}`}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', width: 40, height: 40, flex: '0 0 40px', display: 'grid', placeItems: 'center' }}
                    >
                      <Icono nombre="close" size={16} color={c.texto3} />
                    </button>
                  </motion.div>
                ))}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
          </AnimatePresence>
        </div>

        {/* -------------------------------------------------------- vista previa */}
        <div className="lg:sticky lg:top-3" style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <AnimatePresence initial={false}>
            {noEntran > 0 && (
              <motion.div {...abrir(8)} style={{ ...aviso('warn'), fontSize: 13 }}>
                El código de barras no entra en {noEntran === total ? 'ningún pallet' : `${noEntran} de ${total} pallets`}:
                va a salir con el aviso en vez del código.
              </motion.div>
            )}
          </AnimatePresence>

          <div style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>
            <Numero valor={total} desdeCero={false} /> {total === 1 ? 'pallet' : 'pallets'} · <Numero valor={cantEtiquetas} desdeCero={false} /> {cantEtiquetas === 1 ? 'etiqueta' : 'etiquetas'} de 100×150
          </div>

          {total === 0 ? (
            <div style={{ fontSize: 12.5, color: c.texto3 }}>Elegí algún contenedor, o cargá un pallet suelto.</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(90px,1fr))', gap: 8 }}>
                <AnimatePresence initial={false} mode="popLayout">
                {preview.map((par, i) => (
                  <motion.div
                    key={`${par[0].id}-${i}`}
                    layout
                    {...fila(i)}
                    style={{ border: `1px solid ${c.borde}`, borderRadius: 6, overflow: 'hidden', lineHeight: 0, aspectRatio: '100 / 150' }}
                    dangerouslySetInnerHTML={{ __html: svgEtiquetaPallets(par) }}
                  />
                ))}
                </AnimatePresence>
              </div>
              {cantEtiquetas > preview.length && (
                <div style={{ fontSize: 12.5, color: c.texto3 }}>y {cantEtiquetas - preview.length} más</div>
              )}
            </>
          )}

          <button
            type="button"
            onClick={() => void imprimir()}
            disabled={cantEtiquetas === 0}
            style={{ ...botonPrimario, minHeight: 44, padding: '0 18px', opacity: cantEtiquetas === 0 ? 0.55 : 1, justifyContent: 'center' }}
          >
            <Icono nombre="print" size={19} />
            <span>
              Imprimir <Numero valor={cantEtiquetas} desdeCero={false} /> {cantEtiquetas === 1 ? 'etiqueta' : 'etiquetas'}
            </span>
          </button>
        </div>
      </div>
      {dialogo}
    </div>
  );
}
